import React, { useEffect, useState } from "react";
import { Box, Typography, Button, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchApplicationCategory, fetchApplicationType, addApplicationCategoryCriteriaValue } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function ApplicationCategoryClassificationCriteriaAddPage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [criterias, setCriterias] = useState([]);
  const [criteriaValues, setCriteriaValues] = useState([]);
  const [selectedCriteria, setSelectedCriteria] = useState("");
  const [selectedValue, setSelectedValue] = useState("");
  const [allValues, setAllValues] = useState([]);

  const basePath = `/strategy/${businessDomainId}/application-categories/${categoryId}`;

  useEffect(() => {
    (async () => {
      try {
        const cat = await fetchApplicationCategory(categoryId);
        const appTypes = cat.associatedApplicationTypes || [];
        let ccs = [];
        let vals = [];
        for (const at of appTypes) {
          const mt = await fetchApplicationType(at.applicationTypeId);
          ccs = ccs.concat(mt.classificationCriterias || []);
          vals = vals.concat(mt.classificationCriteriaValues || []);
        }
        setCriterias(ccs);
        setAllValues(vals);
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const handleCriteriaChange = (e) => {
    const name = e.target.value;
    setSelectedCriteria(name);
    setSelectedValue("");
    setCriteriaValues(
      allValues.filter((v) => v.classificationCriteria?.classificationCriteriaName === name)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCriteria || !selectedValue) {
      notify.error(t("applicationCategory.classificationCriteria.selectRequired"));
      return;
    }
    try {
      const value = criteriaValues.find((v) => v.classificationCriteriaValueName === selectedValue);
      if (!value) return;
      const res = await addApplicationCategoryCriteriaValue(categoryId, value);
      if (res?.result === "NG") {
        notify.error(res.errorMessage || t("message.create.error"));
        return;
      }
      notify.success(t("message.create.success"));
      navigate(`${basePath}/classification-criteria`);
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("applicationCategory.classificationCriteriaValues")}：{t("common.add")}
      </Typography>
      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 480 }}>
        <FormControl fullWidth required sx={{ mb: 3 }}>
          <InputLabel>{t("applicationCategory.classificationCriteriaName")}</InputLabel>
          <Select value={selectedCriteria} onChange={handleCriteriaChange}
            label={t("applicationCategory.classificationCriteriaName")}>
            {criterias.map((c) => (
              <MenuItem key={c.classificationCriteriaName} value={c.classificationCriteriaName}>
                {c.classificationCriteriaName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth required sx={{ mb: 3 }} disabled={!selectedCriteria}>
          <InputLabel>{t("applicationCategory.classificationCriteriaValueName")}</InputLabel>
          <Select value={selectedValue} onChange={(e) => setSelectedValue(e.target.value)}
            label={t("applicationCategory.classificationCriteriaValueName")}>
            {criteriaValues.map((v) => (
              <MenuItem key={v.classificationCriteriaValueName} value={v.classificationCriteriaValueName}>
                {v.classificationCriteriaValueName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.add")}</Button>
          <Button variant="outlined" onClick={() => navigate(`${basePath}/classification-criteria`)}>
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
